/**
 * Translation Modes: Me, Other and Both (feature guide)
 *
 * Screenshots were taken from the Sokuji v0.44.0 desktop app (01-mode-picker.png
 * and 02-conversation.png from v0.41.1; those views have not changed since).
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const en: TutorialData = {
  "pageTitle": "Translation Modes: Me, Other and Both",
  "backLink": "Back to Documentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Overview",
    "content": "Sokuji translates in one of three directions. You choose it with the mode switch at the bottom left of the main window, or the setup guide sets it from the scenario you pick. The mode cannot change while a session is running."
  },
  "steps": [
    {
      "title": "Choose a mode",
      "content": "<strong>Me</strong> translates your voice for the other side: they hear it through the virtual microphone (unless <strong>Translation the other side hears</strong> is off) and you see it as subtitles. <strong>Other</strong> translates the other side’s voice from <a href=\"/docs/features/participant-audio\">Other’s audio</a> for you: as subtitles, and also read aloud to you when <strong>Translation I hear</strong> is on. <strong>Both</strong> does the two at the same time. See <a href=\"/docs/features/text-only\">Spoken or Text Only</a>.",
      "screenshot": "01-mode-picker.png"
    },
    {
      "title": "Read the conversation",
      "content": "Your lines are marked <strong>Me</strong> in green and the other side’s <strong>Other</strong> in orange. Each line shows the original speech in grey with its language, and the translation below it.",
      "screenshot": "02-conversation.png"
    },
    {
      "title": "Check the devices a mode uses",
      "content": "Click the selected mode to see the devices it uses and switch them on or off: <strong>Me</strong> shows your microphone and <strong>I hear it too</strong>, <strong>Other</strong> shows Other’s audio and <strong>Translation I hear</strong>, and <strong>Both</strong> shows your microphone and Other’s audio. With Soniox (your own key, or KizunaAI’s Soniox engine), Both also asks where the <strong>Other side</strong> is: <strong>In a meeting</strong> or <strong>Beside me</strong>. <strong>Full settings →</strong> opens the <strong>Speech</strong> block in Settings.",
      "screenshot": "03-mode-devices.png"
    },
    {
      "title": "Tell speakers apart",
      "content": "With Soniox (your own key, or KizunaAI’s Soniox engine), once two or more people are heard on the other side, each one gets a numbered avatar and a name, <strong>Speaker 1</strong>, <strong>Speaker 2</strong> and so on, instead of Other. The numbering starts again with each session.",
      "screenshot": "04-speaker-labels.png"
    },
    {
      "title": "Face-to-face: Both, beside me",
      "content": "For two people at one computer, choose <strong>Both</strong> and set the Other side to <strong>Beside me</strong>. You share your microphone and no system audio is captured. Each of you wears one earbud and hears only the translation into your own language: by default you in the left ear and the other person in the right. To swap them, pick the channels of Translation the other side hears and Translation I hear in the Speech block. Each translated line shows <strong>L</strong> or <strong>R</strong> for the ear it played in. A translation that is not in the listener’s language is read to no one and shows a muted speaker instead.",
      "screenshot": "05-face-to-face.png"
    }
  ],
  "tips": {
    "title": "Tips",
    "items": [
      "Translation the other side hears and Translation I hear decide which directions are also read aloud.",
      "I hear it too, which lets you hear your translated voice yourself, works in Me and Both modes. In Both it is off while Other’s audio captures all system sound (desktop app), and face-to-face it is not offered.",
      "Face-to-face needs headphones, one side each: a speaker lets the microphone pick up the translation and translate it again.",
      "To change the mode or the other side, stop the session first."
    ]
  },
  "faq": {
    "title": "Frequently Asked Questions",
    "items": [
      {
        "question": "Which mode do I need?",
        "answer": "To follow what others say: Other. To be understood: Me. For a conversation in both directions online: Both, in a meeting. For two people at one computer: Both, beside me. The KizunaAI guides walk through each case step by step."
      },
      {
        "question": "Why don’t I see Beside me?",
        "answer": "Only Soniox, with your own key or as KizunaAI’s Soniox engine, can translate two people at one microphone. With another provider, Both always translates Other’s audio."
      },
      {
        "question": "Why is a mode greyed out during a session?",
        "answer": "The mode is locked while a session runs. Stop the session, change the mode, and start again."
      }
    ]
  },
  "troubleshooting": {
    "title": "Troubleshooting",
    "content": "Still stuck? Send us feedback from the account menu, or write to <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. You can also ask in <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const zh: TutorialData = {
  "pageTitle": "翻译模式：我、对方和两者",
  "backLink": "返回文档",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "概述",
    "content": "Sokuji 可以朝三个方向之一进行翻译。你可以用主窗口左下角的模式切换来选择，也可以由设置向导根据你选择的场景自动设置。会话运行期间无法更改模式。"
  },
  "steps": [
    {
      "title": "选择模式",
      "content": "<strong>我</strong>模式把你的话翻译给对方：对方通过虚拟麦克风听到翻译语音（除非关闭了<strong>对方听到的翻译</strong>），你则看到字幕。<strong>对方</strong>模式把<a href=\"/docs/features/participant-audio\">对方音频</a>中对方的话翻译给你：以字幕显示，开启<strong>我听到的翻译</strong>时还会朗读给你听。<strong>两者</strong>模式则同时进行这两种翻译。请参阅<a href=\"/docs/features/text-only\">朗读或仅文字</a>。",
      "screenshot": "01-mode-picker.png"
    },
    {
      "title": "阅读对话",
      "content": "你的发言标记为绿色的<strong>我</strong>，对方的发言标记为橙色的<strong>对方</strong>。每条发言以灰色显示原话及其语言，下方是译文。",
      "screenshot": "02-conversation.png"
    },
    {
      "title": "查看模式使用的设备",
      "content": "点击当前选中的模式，即可查看它使用的设备并将其开启或关闭：<strong>我</strong>显示麦克风和<strong>我也听</strong>，<strong>对方</strong>显示对方音频和<strong>我听到的翻译</strong>，<strong>两者</strong>显示麦克风和对方音频。使用 Soniox（自己的密钥，或 KizunaAI 的 Soniox 引擎）时，“两者”还会询问<strong>对方在哪里</strong>：<strong>在线会议</strong>或<strong>就在身边</strong>。<strong>完整设置 →</strong>会打开设置中的<strong>语音</strong>区块。",
      "screenshot": "03-mode-devices.png"
    },
    {
      "title": "区分不同的说话人",
      "content": "使用 Soniox（自己的密钥，或 KizunaAI 的 Soniox 引擎）时，一旦在对方那边听到两个或更多人，每个人都会有一个带编号的头像和名称：<strong>说话人 1</strong>、<strong>说话人 2</strong>，依此类推，取代“对方”。每次会话都会重新编号。",
      "screenshot": "04-speaker-labels.png"
    },
    {
      "title": "面对面：两者，就在身边",
      "content": "两个人共用一台电脑时，选择<strong>两者</strong>，并把“对方在哪里”设为<strong>就在身边</strong>。你们共用你的麦克风，不会捕获系统音频。每人戴一只耳机，只听翻译成自己语言的那一路：默认你在左耳，对方在右耳。要对调，在语音区块中为“对方听到的翻译”和“我听到的翻译”选择声道。每条译文会显示 <strong>L</strong> 或 <strong>R</strong>，表示它在哪只耳朵播放。如果某条译文不是听者的语言，就不会读给任何人听，并显示一个静音图标。",
      "screenshot": "05-face-to-face.png"
    }
  ],
  "tips": {
    "title": "提示",
    "items": [
      "“对方听到的翻译”和“我听到的翻译”决定哪些方向还会被朗读出来。",
      "“我也听”能让你自己也听到翻译语音，在“我”和“两者”模式下可用。在“两者”模式下，当对方音频正在捕获整个系统的声音时（桌面应用），它会关闭；面对面时不提供这一项。",
      "面对面需要耳机，一人戴一侧：用扬声器的话，麦克风会拾取译音并再次翻译。",
      "要更改模式或“对方在哪里”，请先停止会话。"
    ]
  },
  "faq": {
    "title": "常见问题",
    "items": [
      {
        "question": "我需要哪种模式？",
        "answer": "想听懂别人说的话：对方。想让别人听懂你：我。在线双向对话：两者，在线会议。两个人共用一台电脑：两者，就在身边。KizunaAI 指南会逐步介绍每种情况。"
      },
      {
        "question": "为什么看不到“就在身边”？",
        "answer": "只有 Soniox（使用自己的密钥，或作为 KizunaAI 的 Soniox 引擎）能翻译共用一个麦克风的两个人。使用其他服务商时，“两者”总是翻译对方音频。"
      },
      {
        "question": "为什么会话期间某个模式是灰色的？",
        "answer": "会话运行期间模式处于锁定状态。停止会话，更改模式，然后重新开始。"
      }
    ]
  },
  "troubleshooting": {
    "title": "故障排除",
    "content": "仍然无法解决？可以从账号菜单向我们发送反馈，或发送邮件至 <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>。你也可以在 <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> 中提问。"
  }
};

const ja: TutorialData = {
  "pageTitle": "翻訳モード：自分・相手・両方",
  "backLink": "ドキュメントに戻る",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "概要",
    "content": "Sokuji は 3 つの方向のいずれかで翻訳します。メイン画面左下のモード切り替えで選ぶか、セットアップガイドで選んだシナリオに応じて自動で設定されます。セッションの実行中はモードを変更できません。"
  },
  "steps": [
    {
      "title": "モードを選ぶ",
      "content": "<strong>自分</strong>モードはあなたの声を相手向けに翻訳します。相手には仮想マイクを通じて聞こえ（<strong>相手に聞こえる翻訳</strong>がオフの場合を除く）、あなたには字幕で表示されます。<strong>相手</strong>モードは<a href=\"/docs/features/participant-audio\">相手の音声</a>から、相手の声をあなた向けに翻訳します。字幕で表示され、<strong>自分に聞こえる翻訳</strong>がオンなら読み上げも行われます。<strong>両方</strong>モードはその 2 つを同時に行います。<a href=\"/docs/features/text-only\">読み上げまたはテキストのみ</a>もご覧ください。",
      "screenshot": "01-mode-picker.png"
    },
    {
      "title": "会話を読む",
      "content": "あなたの発言は緑色の<strong>自分</strong>、相手の発言はオレンジ色の<strong>相手</strong>で示されます。各発言には元の音声がその言語とともに灰色で表示され、その下に翻訳が表示されます。",
      "screenshot": "02-conversation.png"
    },
    {
      "title": "モードが使うデバイスを確認する",
      "content": "選択中のモードをクリックすると、使用するデバイスが表示され、オン・オフを切り替えられます。<strong>自分</strong>モードではマイクと<strong>自分にも再生</strong>、<strong>相手</strong>モードでは相手の音声と<strong>自分に聞こえる翻訳</strong>、<strong>両方</strong>モードではマイクと相手の音声が表示されます。Soniox（自分の API キー、または KizunaAI の Soniox エンジン）では、両方モードで<strong>相手の場所</strong>も尋ねられます。<strong>オンライン会議</strong>か<strong>隣にいる</strong>かです。<strong>すべての設定 →</strong>で設定の<strong>音声</strong>ブロックが開きます。",
      "screenshot": "03-mode-devices.png"
    },
    {
      "title": "話者を区別する",
      "content": "Soniox（自分の API キー、または KizunaAI の Soniox エンジン）では、相手側で 2 人以上の声が聞こえると、それぞれに番号付きのアバターと名前（<strong>話者 1</strong>、<strong>話者 2</strong>など）が付き、「相手」の代わりに表示されます。番号はセッションごとに振り直されます。",
      "screenshot": "04-speaker-labels.png"
    },
    {
      "title": "対面：両方、隣にいる",
      "content": "1 台のパソコンを 2 人で使う場合は、<strong>両方</strong>を選び、相手の場所を<strong>隣にいる</strong>に設定します。あなたのマイクを 2 人で共有し、システム音声はキャプチャされません。それぞれがイヤホンを片耳ずつ着け、自分の言語への翻訳だけを聞きます。既定では、あなたが左耳、相手が右耳です。入れ替えるには、「音声」ブロックで「相手に聞こえる翻訳」と「自分に聞こえる翻訳」のチャンネルを選びます。翻訳された各行には、再生された耳を示す <strong>L</strong> または <strong>R</strong> が表示されます。聞き手の言語になっていない翻訳は誰にも読み上げられず、代わりにミュートのスピーカーアイコンが表示されます。",
      "screenshot": "05-face-to-face.png"
    }
  ],
  "tips": {
    "title": "ヒント",
    "items": [
      "「相手に聞こえる翻訳」と「自分に聞こえる翻訳」で、どの方向の翻訳を読み上げるかが決まります。",
      "翻訳音声を自分でも聞ける「自分にも再生」は、自分モードと両方モードで機能します。両方モードでは、相手の音声がシステム全体の音をキャプチャしている間（デスクトップアプリ）はオフになり、対面では表示されません。",
      "対面ではヘッドホンを使い、片側ずつ分けて着けてください。スピーカーで鳴らすと、マイクが翻訳音声を拾ってもう一度翻訳してしまいます。",
      "モードや相手の場所を変更するには、先にセッションを停止してください。"
    ]
  },
  "faq": {
    "title": "よくある質問",
    "items": [
      {
        "question": "どのモードを使えばよいですか？",
        "answer": "相手の話を理解したいなら相手、自分の話を理解してもらいたいなら自分です。オンラインで双方向に会話するなら両方（オンライン会議）、1 台のパソコンを 2 人で使うなら両方（隣にいる）です。KizunaAI のガイドでそれぞれの場合を順を追って説明しています。"
      },
      {
        "question": "「隣にいる」が表示されないのはなぜですか？",
        "answer": "1 つのマイクで 2 人の発言を翻訳できるのは、Soniox（自分の API キー、または KizunaAI の Soniox エンジン）だけです。ほかのプロバイダーでは、両方モードは常に相手の音声を翻訳します。"
      },
      {
        "question": "セッション中にモードがグレーになっているのはなぜですか？",
        "answer": "セッションの実行中はモードがロックされます。セッションを停止してモードを変更し、もう一度開始してください。"
      }
    ]
  },
  "troubleshooting": {
    "title": "トラブルシューティング",
    "content": "それでも解決しない場合は、アカウントメニューからフィードバックを送るか、<a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a> までご連絡ください。<a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> で質問することもできます。"
  }
};

const ko: TutorialData = {
  "pageTitle": "번역 모드: 나, 상대방, 모두",
  "backLink": "문서로 돌아가기",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "개요",
    "content": "Sokuji는 세 가지 방향 중 하나로 번역합니다. 메인 창 왼쪽 아래의 모드 전환에서 선택하거나, 설정 가이드에서 고른 시나리오에 따라 자동으로 설정됩니다. 세션이 실행 중일 때는 모드를 바꿀 수 없습니다."
  },
  "steps": [
    {
      "title": "모드 선택",
      "content": "<strong>나</strong> 모드는 내 음성을 상대방을 위해 번역합니다. 상대방은 가상 마이크를 통해 듣고(<strong>상대방에게 들리는 번역</strong>이 꺼져 있지 않은 경우), 나는 자막으로 봅니다. <strong>상대방</strong> 모드는 <a href=\"/docs/features/participant-audio\">상대방 오디오</a>에서 상대방의 음성을 나를 위해 번역합니다. 자막으로 보여 주고, <strong>내가 듣는 번역</strong>이 켜져 있으면 음성으로도 읽어 줍니다. <strong>모두</strong> 모드는 이 두 가지를 동시에 합니다. <a href=\"/docs/features/text-only\">음성 또는 텍스트만</a>을 참고하세요.",
      "screenshot": "01-mode-picker.png"
    },
    {
      "title": "대화 읽기",
      "content": "내 발화는 초록색 <strong>나</strong>, 상대방의 발화는 주황색 <strong>상대방</strong>으로 표시됩니다. 각 발화에는 원래 음성이 해당 언어와 함께 회색으로 표시되고, 그 아래에 번역이 표시됩니다.",
      "screenshot": "02-conversation.png"
    },
    {
      "title": "모드가 사용하는 장치 확인",
      "content": "선택된 모드를 클릭하면 사용하는 장치를 확인하고 켜거나 끌 수 있습니다. <strong>나</strong> 모드에서는 마이크와 <strong>나도 듣기</strong>, <strong>상대방</strong> 모드에서는 상대방 오디오와 <strong>내가 듣는 번역</strong>, <strong>모두</strong> 모드에서는 마이크와 상대방 오디오가 표시됩니다. Soniox(내 API 키 사용 또는 KizunaAI의 Soniox 엔진)에서는 모두 모드에서 <strong>상대방 위치</strong>도 묻습니다. <strong>온라인 회의</strong> 또는 <strong>내 옆</strong>입니다. <strong>전체 설정 →</strong>을 누르면 설정의 <strong>음성</strong> 블록이 열립니다.",
      "screenshot": "03-mode-devices.png"
    },
    {
      "title": "화자 구분하기",
      "content": "Soniox(내 API 키 사용 또는 KizunaAI의 Soniox 엔진)에서는 상대방 쪽에서 두 명 이상의 목소리가 들리면, 상대방 대신 각 사람에게 번호가 붙은 아바타와 이름(<strong>화자 1</strong>, <strong>화자 2</strong> 등)이 표시됩니다. 번호는 세션마다 새로 매겨집니다.",
      "screenshot": "04-speaker-labels.png"
    },
    {
      "title": "대면 대화: 모두, 내 옆",
      "content": "두 사람이 한 컴퓨터를 함께 쓸 때는 <strong>모두</strong>를 선택하고 상대방 위치를 <strong>내 옆</strong>으로 설정하세요. 내 마이크를 함께 사용하며 시스템 오디오는 캡처하지 않습니다. 두 사람이 이어버드를 한쪽씩 끼고 각자 자기 언어로 된 번역만 듣습니다. 기본적으로 나는 왼쪽 귀, 상대방은 오른쪽 귀입니다. 바꾸려면 “음성” 블록에서 “상대방에게 들리는 번역”과 “내가 듣는 번역”의 채널을 선택하세요. 번역된 각 줄에는 재생된 귀를 나타내는 <strong>L</strong> 또는 <strong>R</strong>이 표시됩니다. 듣는 사람의 언어가 아닌 번역은 아무에게도 읽어 주지 않으며, 대신 음소거된 스피커 아이콘이 표시됩니다.",
      "screenshot": "05-face-to-face.png"
    }
  ],
  "tips": {
    "title": "팁",
    "items": [
      "“상대방에게 들리는 번역”과 “내가 듣는 번역”에 따라 어느 방향을 음성으로도 읽어 줄지가 정해집니다.",
      "번역된 음성을 직접 들을 수 있는 “나도 듣기”는 나 모드와 모두 모드에서 작동합니다. 모두 모드에서는 상대방 오디오가 시스템 전체 소리를 캡처하는 동안(데스크톱 앱) 꺼지며, 대면 대화에서는 제공되지 않습니다.",
      "대면 대화에는 헤드폰이 필요하며, 한쪽씩 나눠 써야 합니다. 스피커를 쓰면 마이크가 번역 음성을 다시 받아 한 번 더 번역합니다.",
      "모드나 상대방 위치를 바꾸려면 먼저 세션을 중지하세요."
    ]
  },
  "faq": {
    "title": "자주 묻는 질문",
    "items": [
      {
        "question": "어떤 모드가 필요한가요?",
        "answer": "다른 사람의 말을 이해하려면 상대방, 내 말을 이해시키려면 나입니다. 온라인 양방향 대화라면 모두(온라인 회의), 두 사람이 한 컴퓨터를 함께 쓴다면 모두(내 옆)입니다. KizunaAI 가이드에서 각 경우를 단계별로 안내합니다."
      },
      {
        "question": "“내 옆”이 보이지 않는 이유는 무엇인가요?",
        "answer": "마이크 하나로 두 사람의 말을 번역할 수 있는 것은 Soniox뿐이며, 내 API 키를 사용하거나 KizunaAI의 Soniox 엔진으로 쓸 수 있습니다. 다른 제공업체에서는 모두 모드가 항상 상대방 오디오를 번역합니다."
      },
      {
        "question": "세션 중에 모드가 회색으로 표시되는 이유는 무엇인가요?",
        "answer": "세션이 실행되는 동안 모드는 잠깁니다. 세션을 중지하고 모드를 바꾼 다음 다시 시작하세요."
      }
    ]
  },
  "troubleshooting": {
    "title": "문제 해결",
    "content": "그래도 해결되지 않나요? 계정 메뉴에서 피드백을 보내거나 <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>로 이메일을 보내 주세요. <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>에서 질문할 수도 있습니다."
  }
};

const es: TutorialData = {
  "pageTitle": "Modos de traducción: Yo, Otro y Ambos",
  "backLink": "Volver a la documentación",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Descripción general",
    "content": "Sokuji traduce en una de tres direcciones. La eliges con el selector de modo, abajo a la izquierda de la ventana principal, o la guía de configuración la fija según el escenario que elijas. El modo no puede cambiar mientras hay una sesión en marcha."
  },
  "steps": [
    {
      "title": "Elige un modo",
      "content": "<strong>Yo</strong> traduce tu voz para la otra parte: la oyen a través del micrófono virtual (salvo que <strong>Traducción que oye la otra parte</strong> esté desactivada) y tú la ves como subtítulos. <strong>Otro</strong> traduce para ti la voz de la otra parte a partir del <a href=\"/docs/features/participant-audio\">Audio del otro</a>: como subtítulos y, cuando <strong>Traducción que yo oigo</strong> está activada, también leída en voz alta para ti. <strong>Ambos</strong> hace las dos cosas a la vez. Consulta <a href=\"/docs/features/text-only\">Con voz o solo texto</a>.",
      "screenshot": "01-mode-picker.png"
    },
    {
      "title": "Lee la conversación",
      "content": "Tus líneas se marcan con <strong>Yo</strong> en verde y las de la otra parte con <strong>Otro</strong> en naranja. Cada línea muestra el habla original en gris con su idioma, y debajo la traducción.",
      "screenshot": "02-conversation.png"
    },
    {
      "title": "Comprueba los dispositivos que usa un modo",
      "content": "Haz clic en el modo seleccionado para ver los dispositivos que usa y activarlos o desactivarlos: <strong>Yo</strong> muestra tu micrófono y <strong>Yo también la oigo</strong>, <strong>Otro</strong> muestra el Audio del otro y <strong>Traducción que yo oigo</strong>, y <strong>Ambos</strong> muestra tu micrófono y el Audio del otro. Con Soniox (tu propia clave, o el motor de Soniox de KizunaAI), Ambos también pregunta <strong>¿Dónde está el otro?</strong>: <strong>En una reunión</strong> o <strong>A mi lado</strong>. <strong>Configuración completa →</strong> abre el bloque <strong>Voz</strong> en Configuración.",
      "screenshot": "03-mode-devices.png"
    },
    {
      "title": "Distingue a los hablantes",
      "content": "Con Soniox (tu propia clave, o el motor de Soniox de KizunaAI), en cuanto se oye a dos o más personas de la otra parte, cada una recibe un avatar numerado y un nombre, <strong>Hablante 1</strong>, <strong>Hablante 2</strong>, etc., en lugar de Otro. La numeración vuelve a empezar en cada sesión.",
      "screenshot": "04-speaker-labels.png"
    },
    {
      "title": "Cara a cara: Ambos, a mi lado",
      "content": "Para dos personas en un mismo ordenador, elige <strong>Ambos</strong> y, como lugar del otro, <strong>A mi lado</strong>. La otra persona comparte tu micrófono y no se captura el audio del sistema. Cada uno lleva un auricular y oye solo la traducción a su propio idioma: de forma predeterminada, tú en el oído izquierdo y la otra persona en el derecho. Para intercambiarlos, elige los canales de Traducción que oye la otra parte y Traducción que yo oigo en el bloque Voz. Cada línea traducida muestra <strong>L</strong> o <strong>R</strong> según el oído en el que sonó. Una traducción que no está en el idioma de quien escucha no se lee a nadie y muestra en su lugar un altavoz silenciado.",
      "screenshot": "05-face-to-face.png"
    }
  ],
  "tips": {
    "title": "Consejos",
    "items": [
      "Traducción que oye la otra parte y Traducción que yo oigo deciden qué direcciones se leen también en voz alta.",
      "Yo también la oigo, que te permite oír tú mismo tu voz traducida, funciona en los modos Yo y Ambos. En Ambos no está disponible mientras el Audio del otro captura todo el sonido del sistema (aplicación de escritorio), y en una conversación cara a cara no se ofrece.",
      "Para hablar cara a cara hacen falta auriculares, un lado para cada uno: con un altavoz, el micrófono capta la traducción y la vuelve a traducir.",
      "Para cambiar el modo o dónde está el otro, detén primero la sesión."
    ]
  },
  "faq": {
    "title": "Preguntas frecuentes",
    "items": [
      {
        "question": "¿Qué modo necesito?",
        "answer": "Para seguir lo que dicen los demás: Otro. Para que te entiendan: Yo. Para una conversación en ambas direcciones en línea: Ambos, en una reunión. Para dos personas en un mismo ordenador: Ambos, a mi lado. Las guías de KizunaAI explican cada caso paso a paso."
      },
      {
        "question": "¿Por qué no veo A mi lado?",
        "answer": "Solo Soniox, con tu propia clave o como el motor de Soniox de KizunaAI, puede traducir a dos personas con un mismo micrófono. Con otro proveedor, Ambos siempre traduce el Audio del otro."
      },
      {
        "question": "¿Por qué aparece un modo en gris durante una sesión?",
        "answer": "El modo está bloqueado mientras hay una sesión en marcha. Detén la sesión, cambia el modo y vuelve a empezar."
      }
    ]
  },
  "troubleshooting": {
    "title": "Solución de problemas",
    "content": "¿Sigues atascado? Envíanos tus comentarios desde el menú de la cuenta o escribe a <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. También puedes preguntar en <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const fr: TutorialData = {
  "pageTitle": "Modes de traduction : Moi, Autre et Les deux",
  "backLink": "Retour à la documentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Aperçu",
    "content": "Sokuji traduit dans l’une de trois directions. Vous la choisissez avec le sélecteur de mode, en bas à gauche de la fenêtre principale, ou le guide de configuration la règle d’après le scénario que vous choisissez. Le mode ne peut pas changer pendant une session."
  },
  "steps": [
    {
      "title": "Choisissez un mode",
      "content": "<strong>Moi</strong> traduit votre voix pour votre interlocuteur : il l’entend via le microphone virtuel (sauf si <strong>Traduction entendue par l'autre</strong> est désactivée) et vous la voyez en sous-titres. <strong>Autre</strong> traduit pour vous la voix de votre interlocuteur à partir de l’<a href=\"/docs/features/participant-audio\">Audio de l'autre</a> : en sous-titres, et aussi lue à voix haute quand <strong>Traduction que j'entends</strong> est activée. <strong>Les deux</strong> fait les deux à la fois. Voir <a href=\"/docs/features/text-only\">Voix ou texte uniquement</a>.",
      "screenshot": "01-mode-picker.png"
    },
    {
      "title": "Lisez la conversation",
      "content": "Vos lignes sont marquées <strong>Moi</strong> en vert, celles de votre interlocuteur <strong>Autre</strong> en orange. Chaque ligne affiche la parole originale en gris avec sa langue, et la traduction en dessous.",
      "screenshot": "02-conversation.png"
    },
    {
      "title": "Vérifiez les périphériques d’un mode",
      "content": "Cliquez sur le mode sélectionné pour voir les périphériques qu’il utilise et les activer ou les désactiver : <strong>Moi</strong> affiche votre microphone et <strong>Je l'entends aussi</strong>, <strong>Autre</strong> affiche l’Audio de l'autre et <strong>Traduction que j'entends</strong>, et <strong>Les deux</strong> affiche votre microphone et l’Audio de l'autre. Avec Soniox (votre propre clé, ou le moteur Soniox de KizunaAI), Les deux demande aussi <strong>Où est l'autre ?</strong> : <strong>En réunion</strong> ou <strong>À côté de moi</strong>. <strong>Paramètres complets →</strong> ouvre le bloc <strong>Voix</strong> des Paramètres.",
      "screenshot": "03-mode-devices.png"
    },
    {
      "title": "Distinguez les intervenants",
      "content": "Avec Soniox (votre propre clé, ou le moteur Soniox de KizunaAI), dès que deux personnes ou plus sont entendues de l’autre côté, chacune reçoit un avatar numéroté et un nom, <strong>Intervenant 1</strong>, <strong>Intervenant 2</strong>, etc., au lieu d’Autre. La numérotation recommence à chaque session.",
      "screenshot": "04-speaker-labels.png"
    },
    {
      "title": "Face à face : Les deux, à côté de moi",
      "content": "Pour deux personnes devant un même ordinateur, choisissez <strong>Les deux</strong>, puis <strong>À côté de moi</strong> pour indiquer où est l’autre. L’autre personne partage votre microphone et aucun audio système n’est capturé. Chacun porte un écouteur et n’entend que la traduction dans sa propre langue : par défaut, vous dans l’oreille gauche et l’autre personne dans la droite. Pour les inverser, choisissez les canaux de Traduction entendue par l'autre et de Traduction que j'entends dans le bloc Voix. Chaque ligne traduite affiche <strong>L</strong> (gauche) ou <strong>R</strong> (droite) selon l’oreille où elle a été lue. Une traduction qui n’est pas dans la langue de son auditeur n’est lue à personne et affiche à la place un haut-parleur barré.",
      "screenshot": "05-face-to-face.png"
    }
  ],
  "tips": {
    "title": "Conseils",
    "items": [
      "Traduction entendue par l'autre et Traduction que j'entends décident quelles directions sont aussi lues à voix haute.",
      "Je l'entends aussi, qui vous permet d’entendre vous-même votre voix traduite, fonctionne en modes Moi et Les deux. En mode Les deux, il est désactivé tant que l’Audio de l'autre capture tout le son du système (application de bureau), et il n’est pas proposé en face à face.",
      "Le face à face exige un casque, un côté chacun : avec un haut-parleur, le microphone capte la traduction et la traduit de nouveau.",
      "Pour changer de mode ou dire où se trouve votre interlocuteur, arrêtez d’abord la session."
    ]
  },
  "faq": {
    "title": "Questions fréquentes",
    "items": [
      {
        "question": "De quel mode ai-je besoin ?",
        "answer": "Pour suivre ce que disent les autres : Autre. Pour être compris : Moi. Pour une conversation en ligne dans les deux sens : Les deux, en réunion. Pour deux personnes devant un même ordinateur : Les deux, à côté de moi. Les guides KizunaAI détaillent chaque cas étape par étape."
      },
      {
        "question": "Pourquoi ne vois-je pas À côté de moi ?",
        "answer": "Seuls Soniox, avec votre propre clé ou comme moteur Soniox de KizunaAI, savent traduire deux personnes sur un seul microphone. Avec un autre fournisseur, Les deux traduit toujours l’Audio de l'autre."
      },
      {
        "question": "Pourquoi un mode est-il grisé pendant une session ?",
        "answer": "Le mode est verrouillé pendant une session. Arrêtez la session, changez de mode, puis relancez-la."
      }
    ]
  },
  "troubleshooting": {
    "title": "Dépannage",
    "content": "Toujours bloqué ? Envoyez-nous un retour depuis le menu du compte, ou écrivez à <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Vous pouvez aussi poser votre question sur <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const ar: TutorialData = {
  "pageTitle": "أوضاع الترجمة: أنا والآخر والكل",
  "backLink": "العودة إلى التوثيق",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "نظرة عامة",
    "content": "يترجم Sokuji في أحد ثلاثة اتجاهات. تختاره من مبدّل الأوضاع أسفل يسار النافذة الرئيسية، أو يضبطه دليل الإعداد حسب السيناريو الذي تختاره. لا يمكن تغيير الوضع أثناء تشغيل جلسة."
  },
  "steps": [
    {
      "title": "اختر وضعًا",
      "content": "وضع <strong>أنا</strong> يترجم صوتك للطرف الآخر: يسمعه عبر الميكروفون الافتراضي (ما لم تكن <strong>الترجمة التي يسمعها الطرف الآخر</strong> متوقفة)، وتراه أنت كترجمة نصية. وضع <strong>الآخر</strong> يترجم لك صوت الطرف الآخر من <a href=\"/docs/features/participant-audio\">صوت الآخر</a>: كترجمة نصية، وتُقرأ لك بصوت عالٍ أيضًا عندما تكون <strong>الترجمة التي أسمعها</strong> مفعّلة. وضع <strong>الكل</strong> يقوم بالأمرين في الوقت نفسه. راجع <a href=\"/docs/features/text-only\">منطوق أو نص فقط</a>.",
      "screenshot": "01-mode-picker.png"
    },
    {
      "title": "اقرأ المحادثة",
      "content": "تُعلَّم جملك بـ<strong>أنا</strong> باللون الأخضر، وجمل الطرف الآخر بـ<strong>الآخر</strong> باللون البرتقالي. يعرض كل سطر الكلام الأصلي باللون الرمادي مع لغته، والترجمة تحته.",
      "screenshot": "02-conversation.png"
    },
    {
      "title": "تحقّق من الأجهزة التي يستخدمها الوضع",
      "content": "انقر على الوضع المحدد لترى الأجهزة التي يستخدمها وتشغّلها أو توقفها: يعرض وضع <strong>أنا</strong> ميكروفونك و<strong>أسمعها أنا أيضًا</strong>، ويعرض وضع <strong>الآخر</strong> صوت الآخر و<strong>الترجمة التي أسمعها</strong>، ويعرض وضع <strong>الكل</strong> ميكروفونك وصوت الآخر. ومع Soniox (بمفتاحك الخاص، أو محرك Soniox في KizunaAI)، يسألك وضع الكل أيضًا عن <strong>مكان الطرف الآخر</strong>: <strong>في اجتماع</strong> أو <strong>بجانبي</strong>. يفتح <strong>الإعدادات الكاملة ←</strong> قسم <strong>الكلام</strong> في الإعدادات.",
      "screenshot": "03-mode-devices.png"
    },
    {
      "title": "ميّز المتحدثين",
      "content": "مع Soniox (بمفتاحك الخاص، أو محرك Soniox في KizunaAI)، ما إن يُسمع شخصان أو أكثر في الطرف الآخر حتى يحصل كل منهم على صورة رمزية مرقّمة واسم، <strong>المتحدث 1</strong> و<strong>المتحدث 2</strong> وهكذا، بدلًا من الآخر. يبدأ الترقيم من جديد مع كل جلسة.",
      "screenshot": "04-speaker-labels.png"
    },
    {
      "title": "وجهًا لوجه: الكل، بجانبي",
      "content": "لشخصين على حاسوب واحد، اختر <strong>الكل</strong> واضبط مكان الطرف الآخر على <strong>بجانبي</strong>. تتشاركان ميكروفونك، ولا يُلتقط صوت النظام. يضع كل منكما سماعة أذن واحدة ولا يسمع إلا الترجمة إلى لغته: افتراضيًا تكون أنت في الأذن اليسرى والشخص الآخر في اليمنى. لتبديلهما، اختر قناتي «الترجمة التي يسمعها الطرف الآخر» و«الترجمة التي أسمعها» في قسم الكلام. يُظهر كل سطر مترجَم <strong>L</strong> أو <strong>R</strong> للأذن التي شُغّل فيها. أما الترجمة التي ليست بلغة المستمع فلا تُقرأ لأحد، ويظهر بدلًا من ذلك رمز مكبّر صوت مكتوم.",
      "screenshot": "05-face-to-face.png"
    }
  ],
  "tips": {
    "title": "نصائح",
    "items": [
      "تحدد «الترجمة التي يسمعها الطرف الآخر» و«الترجمة التي أسمعها» أي الاتجاهين يُقرأ بصوت عالٍ أيضًا.",
      "يعمل خيار «أسمعها أنا أيضًا»، الذي يتيح لك سماع صوتك المترجَم بنفسك، في وضعي أنا والكل. وفي وضع الكل يكون متوقفًا ما دام صوت الآخر يلتقط كل صوت النظام (تطبيق سطح المكتب)، ولا يُعرض في المحادثة وجهًا لوجه.",
      "تحتاج المحادثة وجهًا لوجه إلى سماعات رأس، جانب لكل شخص: فمع مكبّر الصوت يلتقط الميكروفون الترجمة ويترجمها مرة أخرى.",
      "لتغيير الوضع أو مكان الطرف الآخر، أوقف الجلسة أولًا."
    ]
  },
  "faq": {
    "title": "الأسئلة الشائعة",
    "items": [
      {
        "question": "أي وضع أحتاج؟",
        "answer": "لمتابعة ما يقوله الآخرون: الآخر. ليفهمك الآخرون: أنا. لمحادثة في الاتجاهين عبر الإنترنت: الكل، في اجتماع. لشخصين على حاسوب واحد: الكل، بجانبي. تشرح أدلة KizunaAI كل حالة خطوة بخطوة."
      },
      {
        "question": "لماذا لا أرى «بجانبي»؟",
        "answer": "وحده Soniox، سواء بمفتاحك الخاص أو كمحرك Soniox في KizunaAI، يستطيع ترجمة شخصين على ميكروفون واحد. مع أي مزوّد آخر، يترجم وضع الكل دائمًا صوت الآخر."
      },
      {
        "question": "لماذا يظهر أحد الأوضاع باللون الرمادي أثناء الجلسة؟",
        "answer": "يكون الوضع مقفلًا أثناء تشغيل الجلسة. أوقف الجلسة، وغيّر الوضع، ثم ابدأ من جديد."
      }
    ]
  },
  "troubleshooting": {
    "title": "استكشاف الأخطاء وإصلاحها",
    "content": "هل ما زلت تواجه مشكلة؟ أرسل إلينا ملاحظاتك من قائمة الحساب، أو راسلنا على <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. يمكنك أيضًا طرح سؤالك في <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const pt: TutorialData = {
  "pageTitle": "Modos de tradução: Eu, Outro e Ambos",
  "backLink": "Voltar à documentação",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Visão geral",
    "content": "O Sokuji traduz em uma de três direções. Você a escolhe no seletor de modo, no canto inferior esquerdo da janela principal, ou o guia de configuração a define conforme o cenário escolhido. O modo não pode ser alterado durante uma sessão."
  },
  "steps": [
    {
      "title": "Escolha um modo",
      "content": "<strong>Eu</strong> traduz sua voz para a outra pessoa: ela a ouve pelo microfone virtual (a menos que <strong>Tradução que o outro lado ouve</strong> esteja desativada) e você a vê como legendas. <strong>Outro</strong> traduz para você a voz da outra pessoa a partir do <a href=\"/docs/features/participant-audio\">Áudio do outro</a>: como legendas e, quando <strong>Tradução que eu ouço</strong> está ativada, também lida em voz alta para você. <strong>Ambos</strong> faz as duas coisas ao mesmo tempo. Veja <a href=\"/docs/features/text-only\">Com voz ou somente texto</a>.",
      "screenshot": "01-mode-picker.png"
    },
    {
      "title": "Leia a conversa",
      "content": "Suas falas aparecem marcadas como <strong>Eu</strong> em verde, e as da outra pessoa como <strong>Outro</strong> em laranja. Cada fala mostra o original em cinza com o idioma, e a tradução logo abaixo.",
      "screenshot": "02-conversation.png"
    },
    {
      "title": "Confira os dispositivos de um modo",
      "content": "Clique no modo selecionado para ver os dispositivos que ele usa e ativá-los ou desativá-los: <strong>Eu</strong> mostra o seu microfone e <strong>Eu também ouço</strong>, <strong>Outro</strong> mostra o Áudio do outro e <strong>Tradução que eu ouço</strong>, e <strong>Ambos</strong> mostra o seu microfone e o Áudio do outro. Com a Soniox (sua própria chave, ou o mecanismo Soniox da KizunaAI), Ambos também pergunta <strong>Onde está o outro</strong>: <strong>Em uma reunião</strong> ou <strong>Ao meu lado</strong>. <strong>Configurações completas →</strong> abre o bloco <strong>Voz</strong> nas Configurações.",
      "screenshot": "03-mode-devices.png"
    },
    {
      "title": "Diferencie quem fala",
      "content": "Com a Soniox (sua própria chave, ou o mecanismo Soniox da KizunaAI), assim que duas ou mais pessoas são ouvidas do outro lado, cada uma recebe um avatar numerado e um nome, <strong>Falante 1</strong>, <strong>Falante 2</strong> e assim por diante, em vez de Outro. A numeração recomeça a cada sessão.",
      "screenshot": "04-speaker-labels.png"
    },
    {
      "title": "Cara a cara: Ambos, ao meu lado",
      "content": "Para duas pessoas em um mesmo computador, escolha <strong>Ambos</strong> e, como lugar do outro, <strong>Ao meu lado</strong>. A outra pessoa compartilha o seu microfone, e o áudio do sistema não é capturado. Cada um usa um dos fones e ouve só a tradução para o próprio idioma: por padrão, você no ouvido esquerdo e a outra pessoa no direito. Para invertê-los, escolha os canais de Tradução que o outro lado ouve e Tradução que eu ouço no bloco Voz. Cada fala traduzida mostra <strong>L</strong> ou <strong>R</strong> conforme o ouvido em que tocou. Uma tradução que não está no idioma de quem ouve não é lida para ninguém e mostra um alto-falante silenciado no lugar.",
      "screenshot": "05-face-to-face.png"
    }
  ],
  "tips": {
    "title": "Dicas",
    "items": [
      "Tradução que o outro lado ouve e Tradução que eu ouço definem quais direções também são lidas em voz alta.",
      "Eu também ouço, que permite que você mesmo ouça a sua voz traduzida, funciona nos modos Eu e Ambos. Em Ambos, não fica disponível enquanto o Áudio do outro captura todo o som do sistema (aplicativo de desktop) e, em uma conversa cara a cara, não é oferecido.",
      "Uma conversa cara a cara exige fones de ouvido, um lado para cada um: com um alto-falante, o microfone capta a tradução e a traduz de novo.",
      "Para mudar o modo ou onde está o outro, encerre a sessão primeiro."
    ]
  },
  "faq": {
    "title": "Perguntas frequentes",
    "items": [
      {
        "question": "De qual modo eu preciso?",
        "answer": "Para acompanhar o que os outros dizem: Outro. Para ser entendido: Eu. Para uma conversa nos dois sentidos on-line: Ambos, em uma reunião. Para duas pessoas em um mesmo computador: Ambos, ao meu lado. Os guias da KizunaAI mostram cada caso passo a passo."
      },
      {
        "question": "Por que não vejo Ao meu lado?",
        "answer": "Só a Soniox, com a sua própria chave ou como o mecanismo Soniox da KizunaAI, consegue traduzir duas pessoas em um mesmo microfone. Com outro provedor, Ambos sempre traduz o Áudio do outro."
      },
      {
        "question": "Por que um modo aparece esmaecido durante uma sessão?",
        "answer": "O modo fica bloqueado enquanto uma sessão está em andamento. Encerre a sessão, mude o modo e comece de novo."
      }
    ]
  },
  "troubleshooting": {
    "title": "Solução de problemas",
    "content": "Ainda com dificuldades? Envie seu feedback pelo menu da conta ou escreva para <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Você também pode perguntar no <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const ru: TutorialData = {
  "pageTitle": "Режимы перевода: «Я», «Собеседник» и «Оба»",
  "backLink": "Назад к документации",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Обзор",
    "content": "Sokuji переводит в одном из трёх направлений. Его можно выбрать переключателем режимов в левом нижнем углу главного окна, или мастер настройки задаёт его по выбранному сценарию. Пока сеанс запущен, режим изменить нельзя."
  },
  "steps": [
    {
      "title": "Выберите режим",
      "content": "<strong>Я</strong> переводит ваш голос для собеседника: он слышит перевод через виртуальный микрофон (если не выключен параметр <strong>Перевод, который слышит собеседник</strong>), а вы видите его в виде субтитров. <strong>Собеседник</strong> переводит для вас речь собеседника из <a href=\"/docs/features/participant-audio\">аудио собеседника</a>: в виде субтитров, а если включён параметр <strong>Перевод, который слышу я</strong>, перевод ещё и зачитывается вам вслух. <strong>Оба</strong> делает и то и другое одновременно. См. <a href=\"/docs/features/text-only\">Озвучивание или только текст</a>.",
      "screenshot": "01-mode-picker.png"
    },
    {
      "title": "Читайте разговор",
      "content": "Ваши строки отмечены зелёной меткой <strong>Я</strong>, строки собеседника — оранжевой меткой <strong>Собеседник</strong>. Каждая строка показывает исходную речь серым цветом с указанием языка, а под ней — перевод.",
      "screenshot": "02-conversation.png"
    },
    {
      "title": "Проверьте устройства режима",
      "content": "Нажмите выбранный режим, чтобы увидеть устройства, которые он использует, и включить или выключить их: <strong>Я</strong> показывает ваш микрофон и <strong>Я тоже слышу</strong>, <strong>Собеседник</strong> — аудио собеседника и <strong>Перевод, который слышу я</strong>, а <strong>Оба</strong> — ваш микрофон и аудио собеседника. С Soniox (ваш собственный ключ или движок Soniox в KizunaAI) режим «Оба» также спрашивает <strong>Где собеседник</strong>: <strong>На встрече</strong> или <strong>Рядом со мной</strong>. <strong>Все настройки →</strong> открывает блок <strong>Речь</strong> в настройках.",
      "screenshot": "03-mode-devices.png"
    },
    {
      "title": "Различайте говорящих",
      "content": "С Soniox (ваш собственный ключ или движок Soniox в KizunaAI), как только на стороне собеседника слышно двух или более человек, каждый получает пронумерованный аватар и имя — <strong>Говорящий 1</strong>, <strong>Говорящий 2</strong> и так далее — вместо «Собеседник». Нумерация начинается заново в каждом сеансе.",
      "screenshot": "04-speaker-labels.png"
    },
    {
      "title": "Лицом к лицу: «Оба» и «Рядом со мной»",
      "content": "Для двух человек за одним компьютером выберите <strong>Оба</strong> и задайте для параметра «Где собеседник» значение <strong>Рядом со мной</strong>. Вы вдвоём пользуетесь вашим микрофоном, а системный звук не захватывается. Каждый из вас надевает по одному наушнику и слышит только перевод на свой язык: по умолчанию вы — в левом ухе, собеседник — в правом. Чтобы поменять их местами, выберите каналы для «Перевод, который слышит собеседник» и «Перевод, который слышу я» в блоке «Речь». У каждой переведённой строки отображается <strong>L</strong> или <strong>R</strong> — ухо, в котором она прозвучала. Перевод не на языке слушателя никому не зачитывается, и вместо метки показывается значок выключенного динамика.",
      "screenshot": "05-face-to-face.png"
    }
  ],
  "tips": {
    "title": "Советы",
    "items": [
      "«Перевод, который слышит собеседник» и «Перевод, который слышу я» определяют, какие направления ещё и озвучиваются.",
      "Параметр «Я тоже слышу», который позволяет вам самому слышать свой переведённый голос, работает в режимах «Я» и «Оба». В режиме «Оба» он выключен, пока аудио собеседника захватывает весь системный звук (настольное приложение), а при разговоре лицом к лицу недоступен.",
      "Для разговора лицом к лицу нужны наушники, по одной стороне на каждого: через динамик микрофон улавливает перевод и переводит его снова.",
      "Чтобы сменить режим или параметр «Где собеседник», сначала остановите сеанс."
    ]
  },
  "faq": {
    "title": "Часто задаваемые вопросы",
    "items": [
      {
        "question": "Какой режим мне нужен?",
        "answer": "Чтобы понимать, что говорят другие: «Собеседник». Чтобы вас понимали: «Я». Для разговора в обе стороны онлайн: «Оба», «На встрече». Для двух человек за одним компьютером: «Оба», «Рядом со мной». Руководства по KizunaAI пошагово разбирают каждый случай."
      },
      {
        "question": "Почему я не вижу «Рядом со мной»?",
        "answer": "Переводить двух человек на одном микрофоне умеет только Soniox — с вашим собственным ключом или как движок Soniox в KizunaAI. С другим провайдером режим «Оба» всегда переводит аудио собеседника."
      },
      {
        "question": "Почему режим недоступен во время сеанса?",
        "answer": "Пока сеанс запущен, режим заблокирован. Остановите сеанс, смените режим и запустите снова."
      }
    ]
  },
  "troubleshooting": {
    "title": "Устранение неполадок",
    "content": "Всё ещё не получается? Отправьте нам отзыв через меню учётной записи или напишите на <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Также можно задать вопрос в <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const de: TutorialData = {
  "pageTitle": "Übersetzungsmodi: Ich, Gegenüber und Beides",
  "backLink": "Zurück zur Dokumentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Übersicht",
    "content": "Sokuji übersetzt in eine von drei Richtungen. Sie wählen sie mit dem Modusschalter unten links im Hauptfenster, oder die Einrichtung legt sie anhand des gewählten Szenarios fest. Während eine Sitzung läuft, lässt sich der Modus nicht ändern."
  },
  "steps": [
    {
      "title": "Einen Modus wählen",
      "content": "<strong>Ich</strong> übersetzt Ihre Stimme für Ihr Gegenüber: Es hört sie über das virtuelle Mikrofon (sofern <strong>Übersetzung, die das Gegenüber hört</strong> nicht ausgeschaltet ist), und Sie sehen sie als Untertitel. <strong>Gegenüber</strong> übersetzt die Stimme Ihres Gegenübers aus dem <a href=\"/docs/features/participant-audio\">Audio des Gegenübers</a> für Sie: als Untertitel und, wenn <strong>Übersetzung, die ich höre</strong> eingeschaltet ist, auch vorgelesen. <strong>Beides</strong> macht beides gleichzeitig. Siehe <a href=\"/docs/features/text-only\">Gesprochen oder nur Text</a>.",
      "screenshot": "01-mode-picker.png"
    },
    {
      "title": "Das Gespräch lesen",
      "content": "Ihre Zeilen sind grün mit <strong>Ich</strong> markiert, die Ihres Gegenübers orange mit <strong>Gegenüber</strong>. Jede Zeile zeigt das Gesagte im Original grau mit Angabe der Sprache und darunter die Übersetzung.",
      "screenshot": "02-conversation.png"
    },
    {
      "title": "Die Geräte eines Modus prüfen",
      "content": "Klicken Sie auf den ausgewählten Modus, um die Geräte zu sehen, die er verwendet, und um sie ein- oder auszuschalten: <strong>Ich</strong> zeigt Ihr Mikrofon und <strong>Ich höre es auch</strong>, <strong>Gegenüber</strong> zeigt das Audio des Gegenübers und <strong>Übersetzung, die ich höre</strong>, und <strong>Beides</strong> zeigt Ihr Mikrofon und das Audio des Gegenübers. Mit Soniox (eigener Schlüssel oder die Soniox-Engine von KizunaAI) fragt „Beides“ außerdem <strong>Wo ist das Gegenüber?</strong>: <strong>In einem Meeting</strong> oder <strong>Neben mir</strong>. <strong>Vollständige Einstellungen →</strong> öffnet in den Einstellungen den Bereich <strong>Sprache</strong>.",
      "screenshot": "03-mode-devices.png"
    },
    {
      "title": "Sprecher unterscheiden",
      "content": "Mit Soniox (eigener Schlüssel oder die Soniox-Engine von KizunaAI) erhält, sobald auf der anderen Seite zwei oder mehr Personen zu hören sind, jede von ihnen einen nummerierten Avatar und einen Namen, <strong>Sprecher 1</strong>, <strong>Sprecher 2</strong> und so weiter, statt „Gegenüber“. Die Nummerierung beginnt mit jeder Sitzung neu.",
      "screenshot": "04-speaker-labels.png"
    },
    {
      "title": "Von Angesicht zu Angesicht: Beides, neben mir",
      "content": "Für zwei Personen an einem Computer wählen Sie <strong>Beides</strong> und stellen „Wo ist das Gegenüber?“ auf <strong>Neben mir</strong>. Sie teilen sich Ihr Mikrofon, und es wird kein Systemaudio aufgenommen. Jeder von Ihnen trägt einen Ohrhörer und hört nur die Übersetzung in die eigene Sprache: standardmäßig Sie im linken Ohr und die andere Person im rechten. Um das zu tauschen, wählen Sie im Bereich „Sprache“ die Kanäle von „Übersetzung, die das Gegenüber hört“ und „Übersetzung, die ich höre“. Jede übersetzte Zeile zeigt <strong>L</strong> oder <strong>R</strong> für das Ohr, in dem sie abgespielt wurde. Eine Übersetzung, die nicht in der Sprache der zuhörenden Person ist, wird niemandem vorgelesen und zeigt stattdessen einen stummgeschalteten Lautsprecher.",
      "screenshot": "05-face-to-face.png"
    }
  ],
  "tips": {
    "title": "Tipps",
    "items": [
      "„Übersetzung, die das Gegenüber hört“ und „Übersetzung, die ich höre“ legen fest, welche Richtungen auch vorgelesen werden.",
      "„Ich höre es auch“, mit dem Sie Ihre übersetzte Stimme selbst hören, funktioniert in den Modi „Ich“ und „Beides“. In „Beides“ ist es aus, solange das Audio des Gegenübers den gesamten Systemton aufnimmt (Desktop-App), und von Angesicht zu Angesicht wird es nicht angeboten.",
      "Von Angesicht zu Angesicht brauchen Sie Kopfhörer, jeder eine Seite: Über einen Lautsprecher nimmt das Mikrofon die Übersetzung auf und übersetzt sie erneut.",
      "Um den Modus oder die Angabe, wo das Gegenüber ist, zu ändern, beenden Sie zuerst die Sitzung."
    ]
  },
  "faq": {
    "title": "Häufig gestellte Fragen",
    "items": [
      {
        "question": "Welchen Modus brauche ich?",
        "answer": "Um zu verstehen, was andere sagen: Gegenüber. Um verstanden zu werden: Ich. Für ein Online-Gespräch in beide Richtungen: Beides, in einem Meeting. Für zwei Personen an einem Computer: Beides, neben mir. Die KizunaAI-Anleitungen führen Schritt für Schritt durch jeden Fall."
      },
      {
        "question": "Warum sehe ich „Neben mir“ nicht?",
        "answer": "Nur Soniox, mit eigenem Schlüssel oder als Soniox-Engine von KizunaAI, kann zwei Personen an einem Mikrofon übersetzen. Mit einem anderen Anbieter übersetzt „Beides“ immer das Audio des Gegenübers."
      },
      {
        "question": "Warum ist ein Modus während einer Sitzung ausgegraut?",
        "answer": "Der Modus ist gesperrt, solange eine Sitzung läuft. Beenden Sie die Sitzung, ändern Sie den Modus und starten Sie neu."
      }
    ]
  },
  "troubleshooting": {
    "title": "Fehlerbehebung",
    "content": "Kommen Sie nicht weiter? Senden Sie uns Feedback über das Kontomenü oder schreiben Sie an <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Sie können auch in den <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> fragen."
  }
};

const uk: TutorialData = {
  "pageTitle": "Режими перекладу: «Я», «Співрозмовник» і «Обидва»",
  "backLink": "Назад до документації",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Огляд",
    "content": "Sokuji перекладає в одному з трьох напрямків. Його можна вибрати перемикачем режимів у лівому нижньому куті головного вікна, або майстер налаштування задає його за вибраним сценарієм. Поки сеанс запущено, режим змінити не можна."
  },
  "steps": [
    {
      "title": "Виберіть режим",
      "content": "<strong>Я</strong> перекладає ваш голос для співрозмовника: він чує переклад через віртуальний мікрофон (якщо не вимкнено параметр <strong>Переклад, який чує співрозмовник</strong>), а ви бачите його у вигляді субтитрів. <strong>Співрозмовник</strong> перекладає для вас мовлення співрозмовника з <a href=\"/docs/features/participant-audio\">аудіо співрозмовника</a>: у вигляді субтитрів, а якщо ввімкнено параметр <strong>Переклад, який чую я</strong>, переклад ще й зачитується вам уголос. <strong>Обидва</strong> робить і те, і інше одночасно. Див. <a href=\"/docs/features/text-only\">Озвучування або лише текст</a>.",
      "screenshot": "01-mode-picker.png"
    },
    {
      "title": "Читайте розмову",
      "content": "Ваші рядки позначено зеленою міткою <strong>Я</strong>, рядки співрозмовника — помаранчевою міткою <strong>Співрозмовник</strong>. Кожен рядок показує оригінальне мовлення сірим кольором із зазначенням мови, а під ним — переклад.",
      "screenshot": "02-conversation.png"
    },
    {
      "title": "Перевірте пристрої режиму",
      "content": "Натисніть вибраний режим, щоб побачити пристрої, які він використовує, і ввімкнути або вимкнути їх: <strong>Я</strong> показує ваш мікрофон і <strong>Я теж чую</strong>, <strong>Співрозмовник</strong> — аудіо співрозмовника і <strong>Переклад, який чую я</strong>, а <strong>Обидва</strong> — ваш мікрофон і аудіо співрозмовника. З Soniox (ваш власний ключ або рушій Soniox у KizunaAI) режим «Обидва» також запитує <strong>Де співрозмовник</strong>: <strong>На зустрічі</strong> або <strong>Поруч зі мною</strong>. <strong>Усі налаштування →</strong> відкриває блок <strong>Мовлення</strong> в налаштуваннях.",
      "screenshot": "03-mode-devices.png"
    },
    {
      "title": "Розрізняйте мовців",
      "content": "З Soniox (ваш власний ключ або рушій Soniox у KizunaAI), щойно на боці співрозмовника чути двох або більше людей, кожен отримує пронумерований аватар та ім’я — <strong>Мовець 1</strong>, <strong>Мовець 2</strong> і так далі — замість «Співрозмовник». Нумерація починається заново в кожному сеансі.",
      "screenshot": "04-speaker-labels.png"
    },
    {
      "title": "Віч-на-віч: «Обидва» і «Поруч зі мною»",
      "content": "Для двох людей за одним комп’ютером виберіть <strong>Обидва</strong> й задайте для параметра «Де співрозмовник» значення <strong>Поруч зі мною</strong>. Ви вдвох користуєтеся вашим мікрофоном, а системний звук не захоплюється. Кожен із вас надягає по одному навушнику й чує лише переклад своєю мовою: за замовчуванням ви — у лівому вусі, співрозмовник — у правому. Щоб поміняти їх місцями, виберіть канали для «Переклад, який чує співрозмовник» і «Переклад, який чую я» в блоці «Мовлення». Біля кожного перекладеного рядка показано <strong>L</strong> або <strong>R</strong> — вухо, у якому він прозвучав. Переклад не мовою слухача нікому не зачитується, і замість позначки показується значок вимкненого динаміка.",
      "screenshot": "05-face-to-face.png"
    }
  ],
  "tips": {
    "title": "Поради",
    "items": [
      "«Переклад, який чує співрозмовник» і «Переклад, який чую я» визначають, які напрямки ще й озвучуються.",
      "Параметр «Я теж чую», що дає змогу самому чути свій перекладений голос, працює в режимах «Я» та «Обидва». У режимі «Обидва» він вимкнений, поки аудіо співрозмовника захоплює весь системний звук (настільний застосунок), а під час розмови віч-на-віч його немає.",
      "Для розмови віч-на-віч потрібні навушники, по одній стороні на кожного: через динамік мікрофон вловлює переклад і перекладає його знову.",
      "Щоб змінити режим або параметр «Де співрозмовник», спочатку зупиніть сеанс."
    ]
  },
  "faq": {
    "title": "Часті запитання",
    "items": [
      {
        "question": "Який режим мені потрібен?",
        "answer": "Щоб розуміти, що кажуть інші: «Співрозмовник». Щоб вас розуміли: «Я». Для розмови в обидва боки онлайн: «Обидва», «На зустрічі». Для двох людей за одним комп’ютером: «Обидва», «Поруч зі мною». Посібники з KizunaAI покроково розбирають кожен випадок."
      },
      {
        "question": "Чому я не бачу «Поруч зі мною»?",
        "answer": "Перекладати двох людей на одному мікрофоні вміє лише Soniox — з вашим власним ключем або як рушій Soniox у KizunaAI. З іншим провайдером режим «Обидва» завжди перекладає аудіо співрозмовника."
      },
      {
        "question": "Чому режим недоступний під час сеансу?",
        "answer": "Поки сеанс запущено, режим заблоковано. Зупиніть сеанс, змініть режим і запустіть знову."
      }
    ]
  },
  "troubleshooting": {
    "title": "Усунення несправностей",
    "content": "Досі не виходить? Надішліть нам відгук через меню облікового запису або напишіть на <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Також можна запитати в <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const it: TutorialData = {
  "pageTitle": "Modalità di traduzione: Io, Altro ed Entrambi",
  "backLink": "Torna alla documentazione",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Panoramica",
    "content": "Sokuji traduce in una di tre direzioni. La scegli con il selettore di modalità in basso a sinistra nella finestra principale, oppure la imposta la configurazione guidata in base allo scenario che scegli. La modalità non può cambiare mentre una sessione è attiva."
  },
  "steps": [
    {
      "title": "Scegli una modalità",
      "content": "<strong>Io</strong> traduce la tua voce per l’altra parte: la sente tramite il microfono virtuale (a meno che <strong>Traduzione che sente l'altra parte</strong> non sia disattivata) e tu la vedi come sottotitoli. <strong>Altro</strong> traduce per te la voce dell’altra parte a partire dall’<a href=\"/docs/features/participant-audio\">Audio dell'altro</a>: come sottotitoli, e anche letta ad alta voce quando <strong>Traduzione che sento io</strong> è attiva. <strong>Entrambi</strong> fa le due cose contemporaneamente. Vedi <a href=\"/docs/features/text-only\">Voce o solo testo</a>.",
      "screenshot": "01-mode-picker.png"
    },
    {
      "title": "Leggi la conversazione",
      "content": "Le tue righe sono contrassegnate da <strong>Io</strong> in verde, quelle dell’altra parte da <strong>Altro</strong> in arancione. Ogni riga mostra il parlato originale in grigio con la sua lingua, e sotto la traduzione.",
      "screenshot": "02-conversation.png"
    },
    {
      "title": "Controlla i dispositivi usati da una modalità",
      "content": "Fai clic sulla modalità selezionata per vedere i dispositivi che usa e attivarli o disattivarli: <strong>Io</strong> mostra il tuo microfono e <strong>Lo sento anche io</strong>, <strong>Altro</strong> mostra l’Audio dell'altro e <strong>Traduzione che sento io</strong>, ed <strong>Entrambi</strong> mostra il tuo microfono e l’Audio dell'altro. Con Soniox (la tua chiave, o il motore Soniox di KizunaAI), Entrambi chiede anche <strong>Dov'è l'altro</strong>: <strong>In una riunione</strong> o <strong>Accanto a me</strong>. <strong>Impostazioni complete →</strong> apre il blocco <strong>Voce</strong> in Impostazioni.",
      "screenshot": "03-mode-devices.png"
    },
    {
      "title": "Distingui chi parla",
      "content": "Con Soniox (la tua chiave, o il motore Soniox di KizunaAI), quando dall’altra parte si sentono due o più persone, ognuna riceve un avatar numerato e un nome, <strong>Interlocutore 1</strong>, <strong>Interlocutore 2</strong> e così via, al posto di Altro. La numerazione ricomincia a ogni sessione.",
      "screenshot": "04-speaker-labels.png"
    },
    {
      "title": "Faccia a faccia: Entrambi, accanto a me",
      "content": "Per due persone davanti allo stesso computer, scegli <strong>Entrambi</strong> e poi <strong>Accanto a me</strong> come posizione dell’altro. L’altra persona condivide il tuo microfono e l’audio di sistema non viene acquisito. Ciascuno indossa un auricolare e sente solo la traduzione nella propria lingua: per impostazione predefinita tu nell’orecchio sinistro e l’altra persona nel destro. Per scambiarli, scegli i canali di Traduzione che sente l'altra parte e Traduzione che sento io nel blocco Voce. Ogni riga tradotta mostra <strong>L</strong> (sinistra) o <strong>R</strong> (destra), l’orecchio in cui è stata riprodotta. Una traduzione che non è nella lingua di chi ascolta non viene letta a nessuno e mostra invece un altoparlante barrato.",
      "screenshot": "05-face-to-face.png"
    }
  ],
  "tips": {
    "title": "Consigli",
    "items": [
      "Traduzione che sente l'altra parte e Traduzione che sento io decidono quali direzioni vengono anche lette ad alta voce.",
      "Lo sento anche io, che ti permette di sentire tu stesso la tua voce tradotta, funziona nelle modalità Io ed Entrambi. In Entrambi è disattivato mentre l’Audio dell'altro cattura tutto l’audio di sistema (app desktop), e nel faccia a faccia non è disponibile.",
      "Il faccia a faccia richiede le cuffie, un lato a testa: con un altoparlante il microfono capta la traduzione e la traduce di nuovo.",
      "Per cambiare modalità o la posizione dell’altra parte, interrompi prima la sessione."
    ]
  },
  "faq": {
    "title": "Domande frequenti",
    "items": [
      {
        "question": "Di quale modalità ho bisogno?",
        "answer": "Per seguire ciò che dicono gli altri: Altro. Per farti capire: Io. Per una conversazione online in entrambe le direzioni: Entrambi, in una riunione. Per due persone davanti allo stesso computer: Entrambi, accanto a me. Le guide di KizunaAI illustrano ogni caso passo dopo passo."
      },
      {
        "question": "Perché non vedo Accanto a me?",
        "answer": "Solo Soniox, con la tua chiave o come motore Soniox di KizunaAI, sa tradurre due persone con un solo microfono. Con un altro fornitore, Entrambi traduce sempre l’Audio dell'altro."
      },
      {
        "question": "Perché una modalità è disattivata durante una sessione?",
        "answer": "La modalità è bloccata mentre una sessione è attiva. Interrompi la sessione, cambia modalità e ricomincia."
      }
    ]
  },
  "troubleshooting": {
    "title": "Risoluzione dei problemi",
    "content": "Ancora bloccato? Inviaci un feedback dal menu dell’account, oppure scrivi a <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Puoi anche chiedere su <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const translations: Record<Locale, TutorialData> = { en, zh, ja, ko, es, fr, ar, pt, ru, de, uk, it };

export function FeatureTranslationModes() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/features/translation-modes" />;
}
